import React, { useEffect, useRef } from 'react';

const COLORS = ['#FFD700', '#FFB300', '#FF4D4D', '#FF7A00', '#4DA3FF', '#FFFFFF', '#FFE066', '#FF9F1C'];
const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const DiwaliFireworks = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let w = 0, h = 0, raf = 0, launchTimer = 0, alive = true;
    const rockets = [];
    const particles = [];
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const burst = (x, y, color) => {
      const type = pick(['sphere', 'sphere', 'ring', 'willow', 'crackle']);
      const base = w < 640 ? 55 : 95;
      const count = type === 'crackle' ? base * 1.3 : base;
      for (let i = 0; i < count; i++) {
        const angle = type === 'ring' ? (Math.PI * 2 * i) / count : Math.random() * Math.PI * 2;
        const speed = type === 'ring' ? rand(4.2, 4.8) : type === 'willow' ? rand(1.5, 5) : rand(1, 6.5);
        particles.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1,
          decay: type === 'willow' ? rand(0.004, 0.008) : rand(0.009, 0.018),
          gravity: type === 'willow' ? 0.07 : 0.045,
          size: type === 'crackle' ? rand(0.8, 1.6) : rand(1.4, 2.8),
          color: type === 'crackle' && Math.random() < 0.5 ? '#FFFFFF' : (Math.random() < 0.8 ? color : pick(COLORS)),
          twinkle: type === 'crackle' || Math.random() < 0.25
        });
      }
      particles.push({ x, y, vx: 0, vy: 0, alpha: 1, decay: 0.08, gravity: 0, size: 26, color: '#FFFFFF', flash: true });
    };

    const launch = () => {
      const x = rand(w * 0.08, w * 0.92);
      const targetY = rand(h * 0.06, h * 0.42);
      rockets.push({
        x, y: h + 10,
        vx: rand(-0.6, 0.6),
        vy: -rand(9.5, 12.5),
        targetY,
        color: pick(COLORS)
      });
    };

    const schedule = () => {
      launchTimer = window.setTimeout(() => {
        launch();
        if (Math.random() < 0.35) window.setTimeout(launch, rand(120, 420));
        schedule();
      }, rand(650, 1500));
    };

    const loop = () => {
      if (!alive) return;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,0.22)';
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.12;
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = '#FFE9A8';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(r.x - r.vx * 2, r.y - r.vy * 1.6, 1.2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,170,60,0.7)';
        ctx.fill();
        if (r.vy >= -1.5 || r.y <= r.targetY) {
          burst(r.x, r.y, r.color);
          rockets.splice(i, 1);
        }
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.alpha -= p.decay;
        if (p.alpha <= 0) { particles.splice(i, 1); continue; }
        if (p.flash) {
          ctx.globalAlpha = p.alpha * 0.6;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * (1.4 - p.alpha), 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
          continue;
        }
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        const a = p.twinkle ? p.alpha * rand(0.35, 1) : p.alpha;
        ctx.globalAlpha = a;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.globalAlpha = a * 0.25;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 2.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(loop);
    };

    launch();
    schedule();
    raf = requestAnimationFrame(loop);

    const onVisibility = () => {
      if (document.hidden) { cancelAnimationFrame(raf); clearTimeout(launchTimer); }
      else { raf = requestAnimationFrame(loop); schedule(); }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      clearTimeout(launchTimer);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="diwali-fireworks-canvas"
      className="diwali-layer diwali-fireworks"
      aria-hidden="true"
    />
  );
};

export default DiwaliFireworks;
